import { PlasticsMold, PlasticsMoldStatus } from './plastics-mold-family.model';
import { PlasticsMoldingTask } from './plastics-molding-task.model';
import { RotoSchedule } from './roto-schedule.model';
import { UploadedFile } from './uploaded-file.model';

export const MOLD_CHECKUP_SETTINGS_KIND = 'plastics-mold-checkup-settings';
export const MOLD_CHECKUP_STATION_KIND = 'plastics-mold-checkup-station';
export const MOLD_CHECKUP_DAILY_SUMMARY_KIND = 'plastics-mold-checkup-daily-summary';

/** Server cron delivery cursor; notification IDs are reserved once and retained through retries. */
export interface PlasticsMoldCheckupDailySummary {
    id: string;
    dateKey: string | null;
    preventativeCount: number;
    generalCount: number;
    createdOn: Date | null;
    recipients: Array<{ userId: string; notificationId: string }>;
    nextRecipientIndex: number;
    completedOn: Date | null;
}
export type MoldMaintenanceArea = 'preventative' | 'general';
export const MOLD_MAINTENANCE_AREAS: ReadonlyArray<{ id: MoldMaintenanceArea; name: string; spanish: string }> = [
    { id: 'preventative', name: 'Preventative Maintenance', spanish: 'Mantenimiento preventivo' },
    { id: 'general', name: 'General Maintenance', spanish: 'Mantenimiento general' },
];

export interface MoldCheckupDefinition { id: string; label: string; archived: boolean; }
export interface MoldCheckupSpot { id: string; number: number; row: number; column: number; archived: boolean; }
export interface PlasticsMoldPlacement { machineId: string; armId: string; productListId: string; taskId: string; }
export interface PlasticsMoldCheckupSettings {
    id: string;
    enabled: boolean;
    /** Legacy rollout flag; no longer required or checked. */
    placementsReviewed?: boolean;
    defaultInterval: number;
    preventativeEnglish: string;
    preventativeSpanish: string;
    generalEnglish: string;
    generalSpanish: string;
    /** Legacy numbered-layout configuration, retained for historical records only. */
    overflowEnglish: string;
    overflowSpanish: string;
    checklist: MoldCheckupDefinition[];
    fabricationReasons: MoldCheckupDefinition[];
    noticeTags: MoldCheckupDefinition[];
    spots: MoldCheckupSpot[];
    revision: number;
}
export interface PlasticsMoldCheckupStationState {
    id: string;
    revision: number;
    spotAssignments: Record<string, string>;
    openCheckupIds: string[];
}
export interface PlasticsMoldNotice {
    id: string;
    moldId: string;
    tagId: string;
    tagLabel: string;
    text: string;
    files: UploadedFile[];
    createdOn: Date;
    createdById: string;
    clearedOn: Date | null;
    clearedById: string;
    revision: number;
}
export interface PlasticsMoldCheckup {
    id: string;
    moldId: string;
    moldName: string;
    serialNumber: number | null;
    productId: string;
    partNumber: string;
    productImageUrl?: string;
    productThumbnailImageUrl?: string;
    sourceMachineId: string;
    sourceArmId: string;
    sourceTaskId: string;
    status: 'Queued' | 'In progress' | 'Completed';
    outcome: 'Active' | 'Repair' | null;
    /** Missing on legacy cycle-based checkups, which belong to Preventative Maintenance. */
    maintenanceArea?: MoldMaintenanceArea;
    /** Historical numbered destination. New checkups use maintenanceArea. */
    spotId: string | null;
    spotNumber: number | null;
    cycleCountAtRemoval: number;
    cycleCountAtCompletion: number | null;
    baselineAtRemoval: number;
    intervalAtRemoval: number;
    checklist: Array<{ id: string; label: string; done: boolean }>;
    fabricationReasons: MoldCheckupDefinition[];
    selectedReasonIds: string[];
    notices: PlasticsMoldNotice[];
    files: UploadedFile[];
    notes: string;
    createdOn: Date;
    createdById: string;
    startedOn: Date | null;
    startedById: string;
    completedOn: Date | null;
    completedById: string;
    updatedOn: Date;
    updatedById: string;
    /** Operator-confirmed delivery to the current destination; cleared when the destination changes. */
    acknowledgedOn: Date | null;
    acknowledgedById: string;
    repairRequestId: string | null;
    revision: number;
}
export interface MoldCheckupTaskUpdate {
    id: string;
    status: PlasticsMoldingTask['status'];
    moldId: string;
    completedOn: Date | null;
    totalQuantity?: number;
    note?: string;
    kitNeeded?: boolean;
    hotListOrderItemId?: string | null;
}
export type PlasticsMoldCheckupCommand = { requestId: string } & (
    | { type: 'transition'; expectedSchedule: RotoSchedule; schedule: RotoSchedule; taskUpdates: MoldCheckupTaskUpdate[];
        removedMoldIds: string[]; continuations: Array<{ moldId: string; taskId: string }>; supervisorBadgeId?: string;
        /** Administrative queue/task changes never confirm a physical placement change. */
        scheduleOnly?: boolean }
    | { type: 'start'; checkupId: string; revision: number }
    | { type: 'save-draft'; checkupId: string; revision: number; doneItemIds: string[]; reasonIds: string[]; files: UploadedFile[]; notes: string }
    | { type: 'complete'; checkupId: string; revision: number; outcome: 'Active' | 'Repair' }
    | { type: 'move'; checkupId: string; revision: number; area: MoldMaintenanceArea }
    | { type: 'send-to-general-maintenance'; moldId: string; expectedUpdatedOn: Date; productId?: string }
    | { type: 'acknowledge'; checkupId: string; revision: number }
    | { type: 'add-notice'; moldId: string; tagId: string; text: string; files: UploadedFile[] }
    | { type: 'clear-notice'; noticeId: string; revision: number }
    | { type: 'settings'; settings: PlasticsMoldCheckupSettings }
    | { type: 'request-checkups'; moldIds?: string[] }
    | { type: 'edit-mold'; moldId: string; expectedUpdatedOn: Date; status: PlasticsMoldStatus; interval: number | null;
        checkupOnNextRemoval?: boolean;
        metadata?: Pick<PlasticsMold, 'name' | 'customerId' | 'serialNumber' | 'files'> }
);
export interface PlasticsMoldCheckupCommandResult {
    requestId: string;
    checkupIds: string[];
    repairRequestId?: string;
}
export interface PlasticsMoldCheckupCommandReceipt {
    id: string;
    actorId: string;
    fingerprint: string;
    createdOn: Date;
    result: PlasticsMoldCheckupCommandResult;
}

export function defaultMoldCheckupSettings(id = ''): PlasticsMoldCheckupSettings {
    return { id, enabled: false, defaultInterval: 500,
        preventativeEnglish: 'Take to Preventative Maintenance.', preventativeSpanish: 'Lleve al área de mantenimiento preventivo.',
        generalEnglish: 'Take to General Maintenance.', generalSpanish: 'Lleve al área de mantenimiento general.',
        overflowEnglish: '', overflowSpanish: '', checklist: [], fabricationReasons: [], noticeTags: [], spots: [], revision: 0 };
}
export function normalizeMoldCheckupSettings(value: PlasticsMoldCheckupSettings): PlasticsMoldCheckupSettings {
    return { ...defaultMoldCheckupSettings(), ...value };
}
